/**
 * How one assessed part is described in a list: its frames, its damage types,
 * its action.
 *
 * Extracted because the two damaged-parts modals had their own copies and both
 * got the same three things wrong against the client's own portal — which is
 * the reference, since a reviewer compares the two screens side by side.
 */

/**
 * Every frame for a part, damage crops first.
 *
 * The modals showed `damage_images` *or* `part_images`, never both, so a panel
 * with one damage crop and five camera angles showed one image where the
 * portal shows six. Damage crops lead because they are the annotated evidence;
 * the plain angles follow as context.
 *
 * Accepts either shape: `damage_images` are objects, and `part_images` are bare
 * path strings. Reading only `image.picture` — which both modals did — turned
 * every part_image into `undefined` and rendered a broken tag.
 */
const pathOf = (image) => {
  if (!image) return "";
  if (typeof image === "string") return image;
  return image.upload_picture || image.picture || image.image || image.url || "";
};

export const partFrames = (part) => {
  const frames = [
    ...(Array.isArray(part?.damage_images) ? part.damage_images : []),
    ...(Array.isArray(part?.part_images) ? part.part_images : []),
  ];

  const seen = new Set();
  const out = [];
  frames.forEach((image) => {
    const path = pathOf(image);
    if (!path || seen.has(path)) return;
    seen.add(path);
    out.push({
      path,
      side: (typeof image === "object" && image?.side) || part?.side || "",
      damageType: (typeof image === "object" && image?.dam_type) || "",
      annotated: out.length === 0 && Array.isArray(part?.damage_images)
        ? Boolean(part.damage_images.length)
        : false,
    });
  });
  return out;
};

/** "broke, broken, broken" -> ["broke", "broken"] — order kept, blanks dropped. */
export const splitDamageTypes = (value) =>
  (typeof value === "string" ? value.split(",") : [])
    .map((type) => type.trim())
    .filter((type) => type && type !== "-");

/**
 * Drop the fragment left behind by the API's 100-character cap on dam_type.
 *
 * The Bonnet's string is exactly 100 characters: twelve "scratch," (96) and
 * then four more before the field ran out, leaving "…,scratch,scra". Rendered
 * as-is that reads "Scratch, Scra" — two findings where there is one.
 *
 * Identifiable because a truncation is always the LAST token and is always a
 * prefix of a type already in the string. Only that case is dropped: "broke"
 * and "broken" are both values the model genuinely emits, and both survive
 * where they appear in that order.
 */
const dropTruncatedTail = (types) => {
  if (types.length < 2) return types;

  const last = types[types.length - 1].toLowerCase();
  const earlier = types.slice(0, -1);
  const isFragment = earlier.some((type) => {
    const candidate = type.toLowerCase();
    return candidate.length > last.length && candidate.startsWith(last);
  });

  return isFragment ? earlier : types;
};

const titleCase = (value) =>
  value.replace(/\b[a-z]/g, (character) => character.toUpperCase());

/**
 * The damage types for a part, once each.
 *
 * The API reports one entry per detected coordinate, so `dam_type` arrives as
 * "broken,broken,broken,broken" and the modals printed it verbatim — a row of
 * repeated words that says no more than one of them. The client's own portal
 * de-duplicates and title-cases exactly this way, and it is the screen a
 * reviewer is comparing against.
 */
export const damageTypeLabel = (part, fallback = "No damage") => {
  const sources = [
    part?.damage_type,
    ...(Array.isArray(part?.damage_images)
      ? part.damage_images.map((image) => image?.dam_type)
      : []),
    ...(Array.isArray(part?.damage_types) ? [part.damage_types.join(",")] : []),
  ];

  const unique = [];
  sources.forEach((source) => {
    dropTruncatedTail(splitDamageTypes(source)).forEach((type) => {
      const key = type.toLowerCase();
      if (!unique.some((existing) => existing.toLowerCase() === key)) {
        unique.push(type);
      }
    });
  });

  return unique.length ? titleCase(unique.join(", ")) : fallback;
};

/**
 * The recommended action.
 *
 * Read from both spellings: the adapters emit `action`, older payloads `Action`.
 * Checking only the capitalised one printed "No action" next to a panel the
 * same page had already called Replace in its side rail.
 */
export const partActionLabel = (part, fallback = "No action") => {
  const action = part?.Action || part?.action;
  return typeof action === "string" && action.trim() ? action.trim() : fallback;
};

export default partFrames;
